<template>
  <div class="item-detail">
    <template v-if="item">
      <!-- 顶部导航栏 -->
      <van-nav-bar
        :title="item.get('title')"
        left-arrow
        @click-left="$router.back()"
        :border="false"
        class="nav-bar"
        :fixed="true"
      >
        <template #right>
          <div class="nav-actions">
            <van-icon 
              name="edit" 
              size="20" 
              class="action-icon"
              @click="showEditDialog"
            />
            <van-icon 
              name="share-o" 
              size="20" 
              class="action-icon"
              @click="showShareActionSheet"
            />
          </div>
        </template>
      </van-nav-bar>

      <!-- 主要内容区域 -->
      <div class="main-content">
        <!-- 状态卡片 -->
        <div class="status-card">
          <div class="status-header">
            <van-tag 
              :type="item.get('completed') ? 'success' : 'primary'"
              size="medium"
              class="status-tag"
            >
              {{ item.get('completed') ? '已完成' : '进行中' }}
            </van-tag>
            <span class="create-time">{{ formatDate(item.createdAt) }}</span>
          </div>
          <p class="description" v-if="item.get('description')">
            {{ item.get('description') }}
          </p>
          <div class="meta-info" v-if="item.get('category')">
            <van-icon name="label-o" color="#007AFF"/>
            <span>{{ item.get('category').get('name') }}</span>
          </div>
        </div>

        <!-- 进度卡片 -->
        <div class="progress-card">
          <div class="progress-header">
            <h2>完成进度</h2>
            <span class="progress-count">{{ completedSteps }}/{{ totalSteps }}</span>
          </div>
          <div class="progress-bar">
            <div 
              class="progress-fill"
              :style="{ width: (completedSteps / totalSteps * 100) + '%' }"
            ></div>
          </div>
        </div>

        <!-- 步骤列表 -->
        <div class="steps-card">
          <div class="steps-header">
            <h2>步骤列表</h2>
            <span class="progress">{{ completedSteps }}/{{ totalSteps }}</span>
          </div>

          <div class="steps-list" v-if="steps.length > 0">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="step-item"
              :class="{ completed: step.completed }"
            >
              <van-checkbox 
                v-model="step.completed" 
                @change="toggleStepComplete(index)"
                icon-size="20px"
                shape="square"
              >
                <div class="step-content">
                  <span class="step-text">{{ step.content }}</span>
                  <div class="step-actions">
                    <van-icon 
                      name="edit"
                      class="action-icon"
                      @click.stop="openEditStepDialog(index)"
                    />
                    <van-icon 
                      name="delete"
                      class="action-icon"
                      @click.stop="deleteStep(index)"
                    />
                  </div>
                </div>
              </van-checkbox>
            </div>
          </div>

          <van-empty 
            v-else
            description="暂无步骤"
            class="empty-state"
          />
        </div>
      </div>

      <!-- 添加步骤按钮 -->
      <div class="add-step-btn">
        <van-button
          type="primary"
          block
          icon="plus"
          @click="showAddStep = true"
        >
          添加步骤
        </van-button>
      </div>

      <!-- 添加步骤弹窗 -->
      <van-popup 
        v-model:show="showAddStep" 
        position="bottom"
        round
        class="add-step-popup"
      >
        <div class="popup-header">
          <h3>添加步骤</h3>
          <van-icon name="cross" @click="showAddStep = false" />
        </div>
        <van-form @submit="addStep">
          <van-field
            v-model="newStep"
            name="step"
            placeholder="请输入步骤内容"
            :rules="[{ required: true, message: '请填写步骤内容' }]"
            type="textarea"
            rows="3"
            autosize
          />
          <div class="form-actions">
            <van-button 
              block 
              type="primary" 
              native-type="submit"
              round
            >
              确认添加
            </van-button>
          </div>
        </van-form>
      </van-popup>

      <!-- 编辑步骤弹窗 -->
      <van-dialog
        v-model:show="showEditStepDialog"
        title="编辑步骤"
        show-cancel-button
        @confirm="saveStepEdit"
        class="edit-dialog"
      >
        <van-field
          v-model="editingStep.content"
          type="textarea"
          placeholder="请输入步骤内容"
          rows="3"
          autosize
        />
      </van-dialog>

      <!-- 添加编辑对话框 -->
      <van-dialog
        v-model:show="showEdit"
        title="编辑事项"
        show-cancel-button
        @confirm="saveEdit"
        class="edit-dialog"
      >
        <van-form>
          <van-field
            v-model="editForm.title"
            label="标题"
            placeholder="请输入标题"
            :rules="[{ required: true, message: '请填写标题' }]"
          />
          <van-field
            v-model="editForm.description"
            label="描述"
            type="textarea"
            placeholder="请输入描述"
            rows="3"
            autosize
          />
          <van-field
            v-model="editForm.categoryName"
            is-link
            readonly
            label="分类"
            placeholder="点击选择分类"
            @click="showCategoryPicker = true"
          />
        </van-form>
      </van-dialog>

      <!-- 添加分类选择器 -->
      <van-action-sheet
        v-model:show="showCategoryPicker"
        :actions="categoryActions"
        @select="onCategorySelect"
        cancel-text="取消"
        title="选择分类"
      />

      <!-- 添加分享选项 -->
      <van-action-sheet
        v-model:show="showShareSheet"
        :actions="shareActions"
        @select="onShareSelect"
        cancel-text="取消"
        title="分享方式"
      />

      <!-- 添加分享预览 -->
      <van-popup
        v-model:show="showSharePreview"
        round
        position="bottom"
        class="share-preview"
      >
        <div class="preview-content" ref="previewContent">
          <h2>{{ item.get('title') }}</h2>
          <p v-if="item.get('description')">{{ item.get('description') }}</p>
          <div class="preview-steps">
            <div v-for="(step, index) in steps" :key="index" class="preview-step">
              <van-icon :name="step.completed ? 'checked' : 'circle'" />
              <span>{{ step.content }}</span>
            </div>
          </div>
          <div class="preview-footer">
            <span>来自核对助手</span>
          </div>
        </div>
        <div class="preview-actions">
          <van-button type="primary" block @click="saveAsImage">保存图片</van-button>
        </div>
      </van-popup>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AV from 'leancloud-storage'
import { showToast, showDialog } from 'vant'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import { VueDraggableNext } from 'vue-draggable-next'

export default {
  name: 'ItemDetail',
  components: {
    draggable: VueDraggableNext
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const item = ref(null)
    const steps = ref([])
    const showAddStep = ref(false)
    const newStep = ref('')
    const showEdit = ref(false)
    const showCategoryPicker = ref(false)
    const showShareSheet = ref(false)
    const showSharePreview = ref(false)
    const previewContent = ref(null)
    const showStepListEdit = ref(false)
    const showEditStepDialog = ref(false)
    const editingStep = ref({
      index: -1,
      content: ''
    })

    const editForm = ref({
      title: '',
      description: '',
      category: null,
      categoryName: ''
    })

    // 分类选项
    const categoryActions = computed(() => {
      const options = store.state.categories.map(cat => ({
        name: cat.get('name'),
        value: cat.id
      }))
      options.push({ name: '无分类', value: null })
      return options
    })

    // 分享选项
    const shareActions = [
      { name: '生成分享图片', value: 'image' },
      { name: '分享给朋友', value: 'friend' },
      { name: '添加协作者', value: 'collaborate' }
    ]

    // 显示编辑对话框
    const showEditDialog = () => {
      editForm.value = {
        title: item.value.get('title'),
        description: item.value.get('description') || '',
        category: item.value.get('category')?.id || null,
        categoryName: item.value.get('category')?.get('name') || ''
      }
      showEdit.value = true
    }

    // 保存编辑
    const saveEdit = async () => {
      if (!editForm.value.title) return
      
      try {
        item.value.set('title', editForm.value.title)
        item.value.set('description', editForm.value.description)
        
        if (editForm.value.category) {
          const Category = AV.Object.createWithoutData('Category', editForm.value.category)
          item.value.set('category', Category)
        } else {
          item.value.unset('category')
        }
        
        await item.value.save()
        showToast('保存成功')
        showEdit.value = false
      } catch (error) {
        showToast('保存失败')
      }
    }

    // 选择分类
    const onCategorySelect = (action) => {
      editForm.value.category = action.value
      editForm.value.categoryName = action.name
      showCategoryPicker.value = false
    }

    // 显示分享选项
    const showShareActionSheet = () => {
      showShareSheet.value = true
    }

    // 处理分享选择
    const onShareSelect = async (action) => {
      switch (action.value) {
        case 'image':
          showSharePreview.value = true
          break
        case 'friend':
          if (navigator.share) {
            try {
              await navigator.share({
                title: item.value.get('title'),
                text: item.value.get('description'),
                url: window.location.href
              })
            } catch (error) {
              showToast('分享失败')
            }
          } else {
            showToast('您的设备不支持分享功能')
          }
          break
        case 'collaborate':
          // 设置事项为可分享状态
          try {
            item.value.set('shareable', true)
            await item.value.save()
            showToast('已添加到分享列表')
            router.push({
              path: '/share',
              query: { itemId: item.value.id }
            })
          } catch (error) {
            showToast('操作失败')
          }
          break
      }
      showShareSheet.value = false
    }

    // 保存分享图片
    const saveAsImage = async () => {
      try {
        const canvas = await html2canvas(previewContent.value, {
          backgroundColor: '#ffffff'
        })
        const link = document.createElement('a')
        link.download = `${item.value.get('title')}.png`
        link.href = canvas.toDataURL()
        link.click()
        showToast('保存成功')
        showSharePreview.value = false
      } catch (error) {
        showToast('保存失败')
      }
    }

    const completedSteps = computed(() => 
      steps.value.filter(step => step.completed).length
    )
    
    const totalSteps = computed(() => steps.value.length)

    const formatDate = (date) => {
      return dayjs(date).format('YYYY-MM-DD HH:mm')
    }

    const fetchItem = async () => {
      const query = new AV.Query('ChecklistItem')
      query.include('category')
      try {
        item.value = await query.get(route.params.id)
        steps.value = item.value.get('steps') || []
      } catch (error) {
        showToast('获取数据失败')
      }
    }

    const updateItemStatus = async () => {
      const allCompleted = steps.value.length > 0 && steps.value.every(step => step.completed)
      item.value.set('completed', allCompleted)
      try {
        await item.value.save()
      } catch (error) {
        showToast('状态更新失败')
      }
    }

    const toggleStepComplete = async (index) => {
      try {
        item.value.set('steps', steps.value)
        await item.value.save()
        await updateItemStatus()
      } catch (error) {
        showToast('保存失败')
        // 回滚状态
        steps.value[index].completed = !steps.value[index].completed
      }
    }

    const addStep = async () => {
      if (!newStep.value) return
      
      steps.value.push({
        content: newStep.value,
        completed: false
      })

      try {
        item.value.set('steps', steps.value)
        await item.value.save()
        await updateItemStatus()
        showToast('添加成功')
        showAddStep.value = false
        newStep.value = ''
      } catch (error) {
        showToast('添加失败')
        steps.value.pop()
      }
    }

    const editStep = (index) => {
      showDialog({
        title: '删除步骤',
        message: '确认删除该步骤吗？',
        showCancelButton: true,
      }).then(async () => {
        steps.value.splice(index, 1)
        try {
          item.value.set('steps', steps.value)
          await item.value.save()
          await updateItemStatus()
        } catch (error) {
          showToast('删除失败')
        }
      })
    }

    const shareItem = () => {
      // 实分享功能
    }

    const openEditStepDialog = (index) => {
      editingStep.value = {
        index,
        content: steps.value[index].content
      }
      showEditStepDialog.value = true
    }

    const saveStepEdit = async () => {
      if (editingStep.value.index >= 0) {
        steps.value[editingStep.value.index].content = editingStep.value.content
        try {
          item.value.set('steps', steps.value)
          await item.value.save()
          showToast('保存成功')
        } catch (error) {
          showToast('保存失败')
        }
      }
    }

    const saveStepOrder = async () => {
      try {
        item.value.set('steps', steps.value)
        await item.value.save()
        showToast('保存成功')
      } catch (error) {
        showToast('保存失败')
      }
    }

    const deleteStep = async (index) => {
      showDialog({
        title: '删除步骤',
        message: '确认删除该步骤吗？',
        showCancelButton: true,
      }).then(async () => {
        steps.value.splice(index, 1)
        try {
          item.value.set('steps', steps.value)
          await item.value.save()
          await updateItemStatus()
          showToast('删除成功')
        } catch (error) {
          showToast('删除失败')
        }
      })
    }

    onMounted(fetchItem)

    return {
      item,
      steps,
      showAddStep,
      newStep,
      completedSteps,
      totalSteps,
      formatDate,
      toggleStepComplete,
      addStep,
      editStep,
      shareItem,
      showEdit,
      showCategoryPicker,
      showShareSheet,
      showSharePreview,
      previewContent,
      editForm,
      categoryActions,
      shareActions,
      showEditDialog,
      saveEdit,
      onCategorySelect,
      showShareActionSheet,
      onShareSelect,
      saveAsImage,
      showStepListEdit,
      showEditStepDialog,
      editingStep,
      openEditStepDialog,
      saveStepEdit,
      saveStepOrder,
      deleteStep
    }
  }
}
</script>

<style scoped>
.item-detail {
  min-height: 100vh;
  background-color: #f5f5f7;
  padding-top: 46px;
  padding-bottom: 90px;
}

.nav-bar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
}

:deep(.van-nav-bar__title) {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
}

.nav-actions {
  display: flex;
  gap: 16px;
}

.action-icon {
  color: #1d1d1f;
}

.main-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 状态卡片样式 */
.status-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.status-tag {
  font-size: 13px;
  padding: 4px 12px;
}

:deep(.van-tag--primary) {
  background: rgba(0, 122, 255, 0.1);
  color: #007AFF;
  border: none;
}

:deep(.van-tag--success) {
  background: rgba(52, 199, 89, 0.1);
  color: #34C759;
  border: none;
}

.create-time {
  font-size: 13px;
  color: #86868b;
}

.description {
  font-size: 15px;
  line-height: 1.5;
  color: #1d1d1f;
  margin: 0 0 16px;
}

.meta-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #86868b;
  font-size: 14px;
  padding-top: 16px;
  border-top: 1px solid #f5f5f7;
}

/* 进度卡片样式 */
.progress-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-header h2 {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.progress-count {
  font-size: 15px;
  color: #86868b;
}

.progress-bar {
  height: 6px;
  background: #f5f5f7;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #007AFF;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* 步骤列表卡片样式 */
.steps-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
}

.steps-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.steps-header h2 {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  transition: opacity 0.2s;
}

.step-item.completed {
  opacity: 0.6;
}

.step-content {
  display: flex;
  align-items: center;
  width: 100%;
  margin-left: 8px;
  padding-right: 8px;
}

.step-text {
  flex: 1;
  font-size: 15px;
  color: #1d1d1f;
  margin-right: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.action-icon {
  font-size: 18px;
  padding: 4px;
  color: #86868b;
}

.action-icon:active {
  opacity: 0.7;
}

/* 底部按钮样式 */
.add-step-btn {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
}

:deep(.van-button--primary) {
  background: #007AFF;
  border: none;
  height: 44px;
  font-size: 16px;
  border-radius: 10px;
}

/* 弹窗样式 */
.add-step-popup {
  padding: 20px;
  max-height: 90vh;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.popup-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1d1d1f;
}

.form-actions {
  margin-top: 24px;
}

.empty-state {
  padding: 32px 0;
}

:deep(.edit-dialog .van-dialog__header) {
  padding: 20px 0;
  font-weight: 600;
  font-size: 18px;
}

:deep(.edit-dialog .van-field) {
  padding: 16px;
}

.edit-list-btn {
  display: none;
}

.share-preview {
  height: 80vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.preview-content {
  flex: 1;
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  overflow-y: auto;
}

.preview-content h2 {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px;
}

.preview-content p {
  font-size: 15px;
  color: #666;
  margin: 0 0 20px;
  line-height: 1.5;
}

.preview-steps {
  margin: 20px 0;
}

.preview-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  font-size: 16px;
}

.preview-footer {
  margin-top: 20px;
  text-align: center;
  color: #86868b;
  font-size: 14px;
}

.preview-actions {
  margin-top: 20px;
}

.edit-dialog {
  :deep(.van-dialog__header) {
    padding: 20px 0;
    font-weight: 600;
  }
  
  :deep(.van-field) {
    padding: 12px 16px;
  }
}
</style> 